"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight, CheckCircle2 } from "@/components/icons";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import SectionHeader from "@/components/ui/section-header";
import { FREE_TRIAL_URL, STATS } from "@/lib/constants";
import {
  IPComparisonPanel,
  BandwidthRolloverVisual,
} from "@/components/sections/products/residential-proxies/product-highlights/parts";
import dynamic from "next/dynamic";

// German clone of residential-proxies/product-highlights — reuses the same
// visual panels (IPComparisonPanel, GlobeComponent, BandwidthRolloverVisual),
// translated copy.

const GlobeComponent = dynamic(() => import("@/components/sections/shared/globe"), {
  ssr: false,
  loading: () => (
    <div className="bg-foreground/[0.03] aspect-square w-full animate-pulse rounded-full" />
  ),
});

export default function ResidentialProductHighlightsDe() {
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: inViewScrollRootMargin },
          transition: { duration: 0.5, delay },
        };

  return (
    <>
      <section
        id="real-ip-section"
        className="full-bleed band-wash py-section border-border overflow-hidden border-b"
        aria-labelledby="real-ip-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)}>
              <SectionHeader
                eyebrow="Infrastruktur"
                id="real-ip-heading"
                heading="Auf echten Residential-IPs aufgebaut"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Proxidize&apos;s Residential-Proxy-Netzwerk erstreckt sich über {STATS.countries}{" "}
                Länder mit einem umfangreichen IP-Pool, einfacher Per-GB-Abrechnung und ohne
                künstliche Einschränkungen. Jede Verbindung läuft über echte
                Residential-Infrastruktur, bezogen und gepflegt für langfristige Zuverlässigkeit.
              </p>

              <div className="mb-8 grid grid-cols-2 gap-3">
                {[
                  { val: STATS.ips, label: "Residential-IPs", sub: "Ethisch bezogen" },
                  { val: STATS.countries, label: "Länder", sub: "Volle geografische Abdeckung" },
                  { val: STATS.uptime, label: "Plattform-Verfügbarkeit", sub: "Dashboard & API" },
                  { val: "24/7", label: "Support", sub: "Live-Chat und E-Mail" },
                ].map((item) => (
                  <div key={item.label} className="border-border bg-card rounded-xl border p-3">
                    <p className="text-muted-foreground mb-0.5 text-[10px] font-bold tracking-wider uppercase">
                      {item.val}
                    </p>
                    <p className="text-foreground text-sm font-bold">{item.label}</p>
                    <p className="text-muted-foreground mt-0.5 text-[11px]">{item.sub}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeUp(0.12)}>
              <IPComparisonPanel />
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="worldwide-coverage-section"
        className="full-bleed band-wash py-section-sm border-border bg-band relative isolate overflow-hidden border-b"
        aria-labelledby="worldwide-coverage-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="relative order-2 min-w-0 lg:order-1">
              <GlobeComponent ariaLabel="Interaktiver 3D-Globus zur weltweiten Residential-Proxy-Abdeckung" />
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="order-1 lg:order-2">
              <SectionHeader
                eyebrow="Abdeckung"
                id="worldwide-coverage-heading"
                heading="Residential-Proxy-Standorte und Targeting"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Gezieltes Targeting in allen{" "}
                <span className="text-foreground font-semibold">{STATS.countries} Ländern</span>,
                bis auf Stadt- oder ISP-Ebene. Über das Land hinaus jede Anfrage über genau den
                Standort leiten, den der Anwendungsfall benötigt.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="bandwidth-section"
        className="full-bleed band-wash py-section border-border overflow-hidden border-b"
        aria-labelledby="bandwidth-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)}>
              <SectionHeader
                eyebrow="Flexibilität"
                id="bandwidth-heading"
                heading={<>Ihr Datenvolumen gehört Ihnen</>}
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Kaufen Sie das Datenvolumen, das Sie brauchen. Ungenutztes Volumen wird auf den
                nächsten Monat übertragen, und bei einer Kündigung bleibt das verbleibende Guthaben
                im Konto erhalten —{" "}
                <span className="text-foreground font-semibold">
                  Ihr Datenvolumen verfällt nie.
                </span>
              </p>

              <ul className="mb-8 space-y-2.5">
                {[
                  "Ungenutzte GB werden jeden Monat übertragen",
                  "Datenvolumen bleibt auch nach Kündigung verfügbar",
                  "Kein erzwungener Verfall gekaufter Daten",
                  "Einfache Per-GB-Preise, keine versteckten Gebühren",
                  "Jederzeit nach oben oder unten skalieren",
                ].map((feat) => (
                  <li key={feat} className="text-muted-foreground flex items-center gap-3 text-sm">
                    <CheckCircle2
                      size={14}
                      className="text-brand-green shrink-0"
                      aria-hidden="true"
                    />
                    {feat}
                  </li>
                ))}
              </ul>

              <Link
                href={FREE_TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ size: "default" }),
                  "bg-primary text-primary-foreground border-transparent font-semibold"
                )}
              >
                Kostenlos registrieren
                <ArrowRight size={14} className="ml-1.5" aria-hidden="true" />
              </Link>
            </motion.div>

            <motion.div {...fadeUp(0.12)}>
              <BandwidthRolloverVisual />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
